import { AnimatePresence, motion } from "motion/react";
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

import { compactRowClass } from "@/components/threads/shared.tsx";
import { Button } from "@/components/ui/button.tsx";
import { Input } from "@/components/ui/input.tsx";
import { cn } from "@/lib/utils.ts";
import { DISMISS_REASON_LABEL, duplicateOf } from "@/shared/taskRowCopy.ts";
import {
  type DismissReason,
  MAX_DISMISS_REASON_CHARS,
  type SimpleTaskGroup,
  type UserTask,
} from "@/types/userTasks.ts";
import { useRecordDismissalReason, useUndoDismissal } from "./api.ts";

/**
 * How long strips stay once the user stops dismissing. One number, to tune by
 * hand: a burst of dismissals followed by a pause should feel like clearing a
 * desk, the strips sitting quietly and then going together.
 */
export const STRIP_LINGER_MS = 8000;

/** A task just dismissed, and where it sat, so its strip takes its place. */
export type DismissedEntry = {
  task: UserTask;
  groupKey: SimpleTaskGroup["key"];
  index: number;
  /** What the strip says was recorded, once a reason is given. */
  recorded?: string;
  /**
   * The dismissal is still on its way. The strip's reasons and Undo wait for
   * it: either would reach the server before the dismissal and be refused.
   */
  pending?: boolean;
};

type DismissedState = {
  entries: DismissedEntry[];
  add: (entry: DismissedEntry) => void;
  /**
   * Take a strip down. `restored`: its task is back in the list (Undo, or a
   * dismissal that failed), so it fills its own place and the strips below
   * keep theirs; otherwise the task is gone and they each move up one.
   */
  remove: (taskId: string, options?: { restored?: boolean }) => void;
  /** The dismissal saved: the strip's reasons and Undo can act. */
  settle: (taskId: string) => void;
  setRecorded: (taskId: string, recorded: string) => void;
  /** Hold the strips while the user is on one: hovering, or writing a reason. */
  hold: () => () => void;
};

const DismissedContext = createContext<DismissedState | null>(null);

/**
 * The strips the list is showing, and the one timer that clears them.
 *
 * Strips leave together, never one by one: each dismissal restarts the wait,
 * and a hold (a pointer on a strip, an open reason field) stops it. Held in
 * component state only, so a refresh or leaving the page clears them.
 */
export const DismissedStripsProvider = ({ children }: { children: ReactNode }) => {
  const [entries, setEntries] = useState<DismissedEntry[]>([]);
  const [holds, setHolds] = useState(0);
  // When the current wait began: the last dismissal, or the last hold ending,
  // whichever came later. The strips go `STRIP_LINGER_MS` after it.
  const [waitFrom, setWaitFrom] = useState(0);

  // Keyed on whether there are strips, not how many: one leaving (an answer,
  // an Undo) must not restart the wait for the rest. Only a new dismissal, or
  // a hold ending, moves `waitFrom`.
  const anyStrips = entries.length > 0;
  useEffect(() => {
    if (!anyStrips || holds > 0) return;
    const left = Math.max(0, waitFrom + STRIP_LINGER_MS - Date.now());
    const timer = window.setTimeout(() => setEntries([]), left);
    return () => window.clearTimeout(timer);
  }, [anyStrips, holds, waitFrom]);

  const add = useCallback((entry: DismissedEntry) => {
    setEntries((current) => [...current.filter((e) => e.task.id !== entry.task.id), entry]);
    setWaitFrom(Date.now());
  }, []);
  const remove = useCallback((taskId: string, options?: { restored?: boolean }) => {
    setEntries((current) => {
      const gone = current.find((e) => e.task.id === taskId);
      const rest = current.filter((e) => e.task.id !== taskId);
      if (!gone || options?.restored) return rest;
      // Each strip remembers its place counted with the strips above it; one
      // that leaves for good takes that place with it.
      return rest.map((e) =>
        e.groupKey === gone.groupKey && e.index > gone.index ? { ...e, index: e.index - 1 } : e,
      );
    });
  }, []);
  const settle = useCallback((taskId: string) => {
    setEntries((current) =>
      current.map((e) => (e.task.id === taskId ? { ...e, pending: false } : e)),
    );
  }, []);
  const setRecorded = useCallback((taskId: string, recorded: string) => {
    setEntries((current) => current.map((e) => (e.task.id === taskId ? { ...e, recorded } : e)));
  }, []);
  const hold = useCallback(() => {
    setHolds((n) => n + 1);
    let released = false;
    return () => {
      if (released) return;
      released = true;
      setHolds((n) => n - 1);
      // The full wait again from here, so strips do not vanish the moment
      // the pointer leaves one the user lingered on.
      setWaitFrom(Date.now());
    };
  }, []);

  return (
    <DismissedContext.Provider value={{ entries, add, remove, settle, setRecorded, hold }}>
      {children}
    </DismissedContext.Provider>
  );
};

export function useDismissedStrips(): DismissedState {
  const state = useContext(DismissedContext);
  if (!state) throw new Error("useDismissedStrips needs a DismissedStripsProvider");
  return state;
}

/**
 * The category's groups, every one of them and in order, each with its live
 * tasks and each dismissed task's strip put back where the task sat, so
 * nothing below it moves. Empty groups are returned too: the list keeps them
 * mounted so the last row in one can still play its exit.
 */
export function withStrips(
  groups: SimpleTaskGroup[],
  entries: DismissedEntry[],
  order: SimpleTaskGroup["key"][],
  /**
   * Where each task sat when the first strip went up. While strips show, the
   * live tasks keep those places, so a strip stays beside the rows it sat
   * among even if the server has since reordered them; a task new since then
   * goes first.
   */
  frozen?: ReadonlyMap<string, number>,
): { key: SimpleTaskGroup["key"]; rows: ({ task: UserTask } | { strip: DismissedEntry })[] }[] {
  const dismissed = new Set(entries.map((e) => e.task.id));
  const place = (task: UserTask) => frozen?.get(task.id) ?? -1;
  return order.map((key) => {
    const tasks = (groups.find((g) => g.key === key)?.tasks ?? []).filter(
      (task) => !dismissed.has(task.id),
    );
    if (frozen) tasks.sort((a, b) => place(a) - place(b));
    const live = tasks.map((task) => ({ task }));
    const rows: ({ task: UserTask } | { strip: DismissedEntry })[] = [...live];
    const strips = entries.filter((e) => e.groupKey === key).sort((a, b) => a.index - b.index);
    for (const strip of strips) rows.splice(Math.min(strip.index, rows.length), 0, { strip });
    return { key, rows };
  });
}

/** How long an answered strip shows what was recorded before it goes. */
const ANSWERED_HOLD_MS = 700;

/**
 * How an answered strip leaves: it slides off to the right, keeping its place,
 * and then its space closes and the rows below move up, as a done row leaves.
 */
const ANSWERED_EXIT = {
  x: 160,
  opacity: 0,
  height: 0,
  marginBottom: -12,
  transition: {
    x: { duration: 0.6, ease: "easeIn" as const },
    opacity: { delay: 0.1, duration: 0.5 },
    height: { delay: 0.6, duration: 0.3, ease: "easeInOut" as const },
    marginBottom: { delay: 0.6, duration: 0.3, ease: "easeInOut" as const },
  },
};

/** The reasons a strip offers: a suggestion's shades of no, or an open task's two. */
function stripReasons(task: UserTask): DismissReason[] {
  return task.status === "suggested"
    ? ["alreadyDone", "notImportant", "notATask", "other"]
    : ["notATask", "other"];
}

/**
 * Where a dismissed task was: "Dismissed. Why?", the reasons, and Undo. The
 * dismissal has already happened; answering is optional. The same height as
 * the row it replaces.
 */
export const DismissedStrip = ({ entry }: { entry: DismissedEntry }) => {
  const { remove, setRecorded, hold } = useDismissedStrips();
  const record = useRecordDismissalReason();
  const undo = useUndoDismissal();
  const [writing, setWriting] = useState(false);
  const [words, setWords] = useState("");
  // The row's title is struck through first, then the strip takes its place.
  const [struck, setStruck] = useState(false);
  const release = useRef<(() => void) | null>(null);
  const writingRelease = useRef<(() => void) | null>(null);
  const { task } = entry;
  const busy = !!entry.pending || record.isPending || undo.isPending;

  // Answered, the strip has nothing left to ask: it shows what was recorded
  // for a moment and then goes on its own, as a done row does, instead of
  // waiting for the others.
  useEffect(() => {
    if (!entry.recorded) return;
    const timer = window.setTimeout(() => remove(task.id), ANSWERED_HOLD_MS);
    return () => window.clearTimeout(timer);
  }, [entry.recorded, remove, task.id]);

  useEffect(
    () => () => {
      release.current?.();
      writingRelease.current?.();
    },
    [],
  );

  const handleGive = (reason: DismissReason, reasonText?: string) => {
    record.mutate(
      { taskId: task.id, reason, ...(reasonText ? { reasonText } : {}) },
      {
        onSuccess: () =>
          setRecorded(
            task.id,
            reason === "other" && reasonText ? reasonText : DISMISS_REASON_LABEL[reason],
          ),
      },
    );
  };
  const handleStopWriting = () => {
    setWriting(false);
    writingRelease.current?.();
    writingRelease.current = null;
  };

  return (
    <motion.li
      data-dismissed-task-id={task.id}
      layout="position"
      // Takes the row's place without a fade of its own: the title is struck
      // through, then the strip slides in. The strips leave together by
      // fading, out of the flow so the rows below close up by sliding.
      initial={false}
      exit={entry.recorded ? ANSWERED_EXIT : { opacity: 0, position: "absolute", width: "100%" }}
      transition={{ duration: 0.2, ease: "easeOut" }}
      className={cn(compactRowClass, "cursor-default gap-2 bg-muted/60 text-xs")}
      onPointerEnter={() => {
        release.current ??= hold();
      }}
      onPointerLeave={() => {
        release.current?.();
        release.current = null;
      }}
    >
      {/* One then the other: the struck title holds a beat and fades, and
          only then does the strip slide in, rather than one replacing the
          other in a frame. */}
      <AnimatePresence mode="wait">
        {!struck ? (
          <motion.div
            key="struck"
            className="flex min-w-0 flex-1"
            exit={{ opacity: 0, x: -8 }}
            transition={{ duration: 0.25, delay: 0.15, ease: "easeIn" }}
          >
            <StrikeTitle title={task.title} onDone={() => setStruck(true)} />
          </motion.div>
        ) : (
          <motion.div
            key="strip"
            className="flex min-w-0 flex-1 items-center gap-2"
            initial={{ opacity: 0, x: 12 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
          >
            <span className="shrink-0 font-medium">
              {entry.recorded ? `Dismissed: ${entry.recorded}` : "Dismissed."}
            </span>
            {!entry.recorded && !writing && (
              <div className="flex min-w-0 flex-1 items-center gap-1.5 overflow-x-auto">
                <span className="shrink-0 text-muted-foreground">Why?</span>
                {stripReasons(task).map((reason) => (
                  <Button
                    key={reason}
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-6 shrink-0 bg-white px-2 text-xs"
                    data-statsig-name={`tasks-strip-reason-${reason}`}
                    disabled={busy}
                    onClick={() => {
                      if (reason !== "other") return handleGive(reason);
                      writingRelease.current = hold();
                      setWriting(true);
                    }}
                  >
                    {reason === "other" ? "Other…" : DISMISS_REASON_LABEL[reason]}
                  </Button>
                ))}
                {task.status === "suggested" && task.possibleDuplicateOf && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-6 shrink-0 bg-white px-2 text-xs"
                    data-statsig-name="tasks-strip-reason-same-as"
                    disabled={busy}
                    onClick={() => {
                      const duplicate = task.possibleDuplicateOf;
                      if (!duplicate) return;
                      record.mutate(
                        { taskId: task.id, reason: "sameAs", sameAsTaskId: duplicate.id },
                        { onSuccess: () => setRecorded(task.id, duplicateOf(duplicate.title)) },
                      );
                    }}
                  >
                    {duplicateOf(task.possibleDuplicateOf.title)}
                  </Button>
                )}
              </div>
            )}
            {writing && (
              <form
                className="flex min-w-0 flex-1 items-center gap-1.5"
                onSubmit={(e) => {
                  e.preventDefault();
                  const written = words.trim();
                  if (!written) return;
                  handleGive("other", written);
                  handleStopWriting();
                }}
              >
                <Input
                  autoFocus
                  aria-label="Why it was dismissed"
                  className="h-7 min-w-0 flex-1 text-xs"
                  maxLength={MAX_DISMISS_REASON_CHARS}
                  value={words}
                  onChange={(e) => setWords(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Escape") handleStopWriting();
                  }}
                />
                <Button
                  type="submit"
                  size="sm"
                  variant="primary"
                  className="h-6 shrink-0 px-2 text-xs"
                  data-statsig-name="tasks-strip-reason-other-save"
                  disabled={busy || !words.trim()}
                >
                  Save
                </Button>
              </form>
            )}
            {entry.recorded && <span className="min-w-0 flex-1" />}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="ml-auto h-6 shrink-0 px-2 text-xs"
              data-statsig-name="tasks-strip-undo"
              disabled={busy}
              onClick={() =>
                undo.mutate(
                  { taskId: task.id },
                  { onSuccess: () => remove(task.id, { restored: true }) },
                )
              }
            >
              Undo
            </Button>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.li>
  );
};

/**
 * The dismissed task's title, struck through by a line drawn across it, as
 * the moment of dismissing before the strip takes its place.
 */
const StrikeTitle = ({ title, onDone }: { title: string; onDone: () => void }) => {
  // The strip goes on after the line whether or not its animation reports
  // back: skipped (reduced motion) or never started, it would otherwise hold
  // the strip on the struck title for good.
  const finished = useRef(false);
  const finish = useCallback(() => {
    if (finished.current) return;
    finished.current = true;
    onDone();
  }, [onDone]);
  useEffect(() => {
    const timer = window.setTimeout(finish, STRIKE_MS + 100);
    return () => window.clearTimeout(timer);
  }, [finish]);
  return (
    <span className="relative min-w-0 truncate pl-8 text-sm text-muted-foreground">
      {title}
      <motion.span
        aria-hidden
        className="absolute top-1/2 right-0 left-8 h-px origin-left bg-current"
        initial={{ scaleX: 0 }}
        animate={{ scaleX: 1 }}
        // Slow enough to watch the line cross the title.
        transition={{ duration: STRIKE_MS / 1000, ease: "easeInOut" }}
        onAnimationComplete={finish}
      />
    </span>
  );
};

/** How long the line takes to cross the title. */
const STRIKE_MS = 600;
